// --- FILE: src/utils/payrollUtils.js ---
// Lógica de semana de nómina para negocio de sesiones de fotos
// Semana: Viernes 8:40 AM → Jueves 9:00 PM
// Los turnos se asignan a la semana según su hora de INICIO, no de cierre

import { getDay, addDays, subDays, setHours, setMinutes, setSeconds, setMilliseconds } from 'date-fns';

/**
 * Calcula los límites de la semana de nómina para una fecha dada
 * @param {Date} date - Fecha de referencia (por defecto: hoy)
 * @returns {Object} { weekStart: Date, weekEnd: Date }
 */
export const getPayrollWeekBounds = (date = new Date()) => {
  // Crear copia de la fecha para no modificar la original
  const referenceDate = new Date(date);
  
  // Configuración de la semana de nómina
  const WEEK_START_DAY = 5; // Viernes (0=Domingo, 1=Lunes, ..., 5=Viernes)
  const START_HOUR = 8;     // 8 AM
  const START_MINUTE = 40;  // 40 minutos (8:40 AM)
  const END_HOUR = 21;      // 9 PM
  const END_MINUTE = 0;     // 0 minutos (9:00 PM)
  
  // Obtener día de la semana de la fecha de referencia (0=Domingo, 1=Lunes, etc.)
  const currentDay = getDay(referenceDate);
  const currentHour = referenceDate.getHours();
  const currentMinute = referenceDate.getMinutes();
  
  let weekStart;
  
  // Calcular cuántos días hay que retroceder para llegar al viernes más reciente
  let daysToSubtract;
  
  if (currentDay === WEEK_START_DAY) {
    // Es viernes - verificar si ya pasó la hora de inicio
    const currentTime = currentHour * 60 + currentMinute; // minutos desde medianoche
    const startTime = START_HOUR * 60 + START_MINUTE; // 8:40 AM en minutos
    
    if (currentTime >= startTime) {
      // Ya pasaron las 8:40 AM del viernes - la semana actual ya comenzó
      daysToSubtract = 0;
    } else {
      // Aún no llegan las 8:40 AM - pertenecemos a la semana anterior
      daysToSubtract = 7;
    }
  } else if (currentDay < WEEK_START_DAY) {
    // Estamos entre domingo y jueves - retroceder al viernes anterior
    daysToSubtract = currentDay + (7 - WEEK_START_DAY);
  } else {
    // Estamos en sábado - retroceder al viernes anterior (1 día)
    daysToSubtract = currentDay - WEEK_START_DAY;
  }
  
  // Calcular fecha de inicio de la semana (Viernes 8:40 AM)
  weekStart = subDays(referenceDate, daysToSubtract);
  weekStart = setHours(weekStart, START_HOUR);
  weekStart = setMinutes(weekStart, START_MINUTE);
  weekStart = setSeconds(weekStart, 0);
  weekStart = setMilliseconds(weekStart, 0);
  
  // Calcular fecha de fin de la semana (Jueves 9:00 PM - 6 días después del viernes)
  let weekEnd = addDays(weekStart, 6); // 6 días después del viernes = jueves
  weekEnd = setHours(weekEnd, END_HOUR);
  weekEnd = setMinutes(weekEnd, END_MINUTE);
  weekEnd = setSeconds(weekEnd, 59);
  weekEnd = setMilliseconds(weekEnd, 999);
  
  return { weekStart, weekEnd };
};

/**
 * Verifica si una fecha está dentro de la semana de nómina actual
 * @param {Date} date - Fecha a verificar
 * @returns {boolean}
 */
export const isInCurrentPayrollWeek = (date) => {
  const { weekStart, weekEnd } = getPayrollWeekBounds();
  return date >= weekStart && date <= weekEnd;
};

/**
 * Obtiene la semana de nómina anterior
 * @param {Date} date - Fecha de referencia (por defecto: hoy)
 * @returns {Object} { weekStart: Date, weekEnd: Date }
 */
export const getPreviousPayrollWeek = (date = new Date()) => {
  const previousWeekDate = subDays(date, 7);
  return getPayrollWeekBounds(previousWeekDate);
};

/**
 * Obtiene la próxima semana de nómina
 * @param {Date} date - Fecha de referencia (por defecto: hoy)
 * @returns {Object} { weekStart: Date, weekEnd: Date }
 */
export const getNextPayrollWeek = (date = new Date()) => {
  const nextWeekDate = addDays(date, 7);
  return getPayrollWeekBounds(nextWeekDate);
};

/**
 * Determina a qué semana de nómina pertenece un turno basado en su hora de INICIO
 * @param {Date} shiftStartTime - Hora de inicio del turno
 * @returns {Object} { weekStart: Date, weekEnd: Date }
 */
export const getPayrollWeekForShift = (shiftStartTime) => {
  return getPayrollWeekBounds(shiftStartTime);
};

/**
 * Formatea el período de nómina para mostrar en UI
 * @param {Date} date - Fecha de referencia (por defecto: hoy)
 * @returns {string} Ejemplo: "Viernes 19 Jul 8:40 AM - Jueves 25 Jul 9:00 PM"
 */
export const formatPayrollPeriod = (date = new Date()) => {
  const { weekStart, weekEnd } = getPayrollWeekBounds(date);
  
  const startFormatted = weekStart.toLocaleDateString('es-PR', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'short' 
  }) + ' ' + weekStart.toLocaleTimeString('es-PR', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  const endFormatted = weekEnd.toLocaleDateString('es-PR', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'short' 
  }) + ' ' + weekEnd.toLocaleTimeString('es-PR', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  
  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Verifica si es viernes y ya se puede iniciar turno (después de las 8:40 AM)
 * @param {Date} date - Fecha a verificar (por defecto: ahora)
 * @returns {boolean}
 */
export const canStartShiftOnFriday = (date = new Date()) => {
  const dayOfWeek = getDay(date);
  
  if (dayOfWeek !== 5) return true; // No es viernes, siempre se puede
  
  const currentTime = date.getHours() * 60 + date.getMinutes();
  const startTime = 8 * 60 + 40; // 8:40 AM en minutos
  
  return currentTime >= startTime;
};

/**
 * Obtiene información detallada sobre el estado actual de la semana de nómina
 * @param {Date} date - Fecha de referencia (por defecto: ahora)
 * @returns {Object} Información completa del período
 */
export const getPayrollWeekInfo = (date = new Date()) => {
  const { weekStart, weekEnd } = getPayrollWeekBounds(date);
  const now = new Date();
  
  const isWeekActive = now >= weekStart && now <= weekEnd;
  const weekProgress = isWeekActive 
    ? ((now - weekStart) / (weekEnd - weekStart)) * 100 
    : (now > weekEnd ? 100 : 0);
  
  return {
    weekStart,
    weekEnd,
    isActive: isWeekActive,
    progressPercentage: Math.round(weekProgress),
    formattedPeriod: formatPayrollPeriod(date),
    canStartShift: canStartShiftOnFriday(now),
    daysRemaining: isWeekActive ? Math.ceil((weekEnd - now) / (1000 * 60 * 60 * 24)) : 0
  };
};

// Exportar también una función de utilidad para debugging
export const debugPayrollWeek = (date = new Date()) => {
  const info = getPayrollWeekInfo(date);
  console.log('=== DEBUG: Semana de Nómina ===');
  console.log('Fecha de referencia:', date.toLocaleString('es-PR'));
  console.log('Inicio de semana:', info.weekStart.toLocaleString('es-PR'));
  console.log('Fin de semana:', info.weekEnd.toLocaleString('es-PR'));
  console.log('Semana activa:', info.isActive);
  console.log('Progreso:', info.progressPercentage + '%');
  console.log('Puede iniciar turno:', info.canStartShift);
  console.log('Período formateado:', info.formattedPeriod);
  return info;
};